<template>
  <v-app
    :light="isLightTheme"
    :dark="!isLightTheme"
  >
    <v-toolbar app>
      <v-toolbar-title class="headline text-uppercase">
        <v-avatar class="mr-2">
          <v-img :src="logo" :tile="true"/>
        </v-avatar>
        <span v-html="title"></span>
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn
        v-if="hasSession && cartEnabled"
        @click="currentView = 'Cart'"
        color="info"
        outline
        :disabled="false"
        style="min-width: 40px; padding: 0"
      >
        <v-badge right color="orange" :value="itemsCount > 0">
          <span slot="badge">{{ this.itemsCount }}</span>
          <v-icon>shopping_basket</v-icon>
        </v-badge>
      </v-btn>
      <v-btn icon @click.stop="rightDrawer = !rightDrawer" v-if="hasSession">
        <v-avatar color="indigo" size="36">
          <img v-if="avatar" :src="avatar" alt="Account">
          <v-icon v-else dark>account_circle</v-icon>
        </v-avatar>
      </v-btn>
    </v-toolbar>

    <v-content :class="`${!hasSession ? 'login' : ''}`">
      <Login @login="login" v-if="!hasSession"/>
      <template v-else>
        <Cart v-if="currentView === 'Cart' && cartEnabled" :items="items" @clear="clearCart"/>
        <component
          v-if="!!currentView && currentView.name && (!currentView.pinProtected || pinPassed)"
          v-bind:is="currentView.name"
          :key="currentView.name"
          :config="currentView.props"
        ></component>
        <div style="height: 50px"></div>
      </template>
    </v-content>

    <v-navigation-drawer
      temporary
      :right="true"
      v-model="rightDrawer"
      fixed
      app
    >
      <v-toolbar flat>
        <v-list>
          <v-list-tile>
            <v-list-tile-title class="title">{{ $t('Application.application') }}</v-list-tile-title>
          </v-list-tile>
        </v-list>
      </v-toolbar>

      <v-divider></v-divider>

      <v-list dense class="pt-0">
        <v-list-tile @click="$i18n.locale = nextLanguageId" v-if="languageIdList.length > 1">
          <v-list-tile-action>
            <v-img
              :src="flag(currentLangId)"
              width="24"
              height="16"
              style="max-width: 24px"
            />
          </v-list-tile-action>
          <v-list-tile-content>
            <v-list-tile-title>{{ $t('Lang.name', currentLangId) }}</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
        <v-list-tile v-if="changePinAvailable" @click="changePin">
          <v-list-tile-action>
            <v-icon>fiber_pin</v-icon>
          </v-list-tile-action>
          <v-list-tile-content>
            <v-list-tile-title>{{ $t('ChangePin.change') }}</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
        <v-list-tile @click="logout">
          <v-list-tile-action>
            <v-icon>exit_to_app</v-icon>
          </v-list-tile-action>
          <v-list-tile-content>
            <v-list-tile-title>{{ $t('Login.logout') }}</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
        <div class="version">Portal: {{versions.portal || '-'}}</div>
        <div class="version">Instance: {{versions.instance || '-'}}</div>
      </v-list>
    </v-navigation-drawer>
    <v-bottom-nav
      v-if="hasSession"
      :active="getCurrentViewName()"
      :value="true"
      :fixed="true"
    >
      <v-btn
        v-for="item in components()"
        color="teal"
        flat
        :key="item.name"
        :value="item.name"
        @click="() => onTabChange(item.name)"
      >
        <span>{{ $t(item.title) }}</span>
        <v-icon>{{item.icon}}</v-icon>
      </v-btn>

    </v-bottom-nav>

    <v-dialog
      v-model="pinDialog"
      persistent width="200"
      :dark="!isLightTheme"
    >
      <PinInput
        @action="onPinInputAction"
        :pinLength="4"
        :active="pinDialog"
      ></PinInput>
    </v-dialog>

    <v-dialog
      v-model="changePinDialog"
      persistent
      width="400"
      :dark="!isLightTheme"
    >
      <ChangePinInput
        @action="onChangePinAction"
      >
      </ChangePinInput>
    </v-dialog>

    <!-- Error message -->
    <v-snackbar v-model="pinError" color="error" :timeout="5000" top>
      {{$t('Invoices.invalidPinCode')}}
      <v-btn dark flat @click="pinError = false">{{$t('Action.close')}}</v-btn>
    </v-snackbar>

  </v-app>
</template>

<script>
  import Cart from './modules/Cart';
  import Login from './components/Login';
  import PinInput from './components/PinInput';
  import ChangePinInput from './components/ChangePinInput';
  import VList from 'vuetify/lib/components/VList/VList'
  import VListTile from 'vuetify/lib/components/VList/VListTile'

  export default {
    name: 'App',
    components: {
      VListTile,
      VList,
      Cart,
      Login,
      PinInput,
      ChangePinInput,
    },
    mounted: function () {
      this.$root.$on('activate-tab', (tabName) => {
        this.currentView = this.getComponent(tabName);
      });
    },
    data() {
      const currentView = this.mainComponent();
      return {
        rightDrawer: false,
        currentView: currentView,
        newView: undefined,
        items: {},
        pinPassed: false,
        pinDialog: false,
        changePinDialog: false,
        pinError: false,
        logo: this.$root.config.logo || require('./assets/favicon-32x32.png'),
        title: this.$root.config.title,
      };
    },
    props: {
      session: {
        type: Object
      }
    },
    computed: {
      cartEnabled() {
        return this.$root.config.cart === true;
      },
      changePinAvailable() {
        return this.components().some(x => x.pinProtected);
      },
      isLightTheme() {
        return this.$root.config.theme === 'light';
      },
      hasSession() {
        return this.$root.hasSession;
      },
      avatar() {
        return this.$root.avatar;
      },
      itemsCount() {
        Object.keys(this.items)
          .filter(key => this.items[key] === 0)
          .forEach(i => delete this.items[i]);
        return Object.keys(this.items).length;
      },
      languageIdList() {
        return Object.keys(this.$i18n.messages);
      },
      currentLangId() {
        return this.$i18n.locale;
      },
      nextLanguageId() {
        const languages = this.languageIdList;
        if (!languages || languages.length < 2) {
          return languages[0] || '';
        }
        const index = languages.indexOf(this.currentLangId);
        return languages[(index + 1) % languages.length];
      },
      versions() {
        return this.$root.versions || {};
      }
    },
    methods: {
      flag(langId) {
        switch (langId) {
          case 'de': return require(`./assets/flag-de.svg`);
          case 'en': return require(`./assets/flag-en.svg`);
          case 'gb': return require(`./assets/flag-gb.svg`);
        }
        return '';
      },
      mainComponent() {
        return this.components().find(x => x.main) || this.components()[0];
      },
      components() {
        return this.$root.config.menu;
      },
      getCurrentViewName() {
        const name = (this.currentView && this.currentView.name
          ? this.currentView.name
          : this.currentView);
        return (name === 'Cart' ? 'shop' : name) || 'unknown';
      },
      getComponent(name) {
        return this.components().find(x => x.name === name);
      },
      hasMenuComponent(name) {
        return this.$root.config.menu.some(item => item.component === name);
      },
      login(session, persist) {
        this.$root.registerSession(session, persist);
      },
      logout() {
        this.rightDrawer = false;
        this.$root.clearSession();
        this.clearCart();
        this.pinPassed = false;
        this.currentView = this.mainComponent();
      },
      clearCart() {
        this.items = {};
      },
      addToCart(items) {
        Object.keys(items).forEach(itemnum => {
          this.$set(this.items, itemnum, ((this.items || {})[itemnum] || 0) + items[itemnum]);
        });
      },
      changePin() {
        this.changePinDialog = true;
      },
      onPinInputAction(action) {
        this.pinDialog = false;

        if (action === 'success') {
          this.pinPassed = true;
          this.currentView = this.newView;
        } else if (action === 'close') {
          this.refreshView();
        } else {
          this.refreshView();
          this.pinError = true;
        }
      },
      onChangePinAction(changeResult) {
        this.changePinDialog = false;
      },
      refreshView() {
        const lastView = this.currentView;
        this.currentView = undefined;
        this.$nextTick(() => this.currentView = lastView);
      },

      onTabChange(name) {
        const componentConf = this.getComponent(name);
        if (componentConf) {
          if (componentConf.pinProtected && !this.pinPassed) {
            this.pinDialog = true;
            this.newView = componentConf;
          } else {
            this.currentView = componentConf;
          }
        }
      }
    }
  }
</script>
<style>
  .version {
    width: 100%;
    text-align: left;
    padding-left: 16px;
    opacity: .3;
    font-size: 10px;
  }
</style>
